import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useRouteMatch } from 'react-router-dom';
import SettingsIcon from '@material-ui/icons/Settings';

import { FeaturePlanType } from 'api';
import { Capability, LargeTab, PaddedPaper, Queries } from 'components';
import { getAppImage } from 'helpers';
import { useProtectedClient } from 'hooks';
import * as paths from 'paths';
import * as Links from 'Links';

import {
  DepartmentsLocations,
  Disclosures,
  Loader,
  Policies,
  Referrals,
  Settings,
} from '../components/Account';
import { EditableListType } from '../types';

const useStyles = makeStyles(theme => ({
  button: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.grey[700],
    },
  },
  lineHeight: {
    lineHeight: 'normal',
  },
}));

const Account: FC = () => {
  const classes = useStyles();
  const match = useRouteMatch();
  const client = useProtectedClient();
  const { t } = useTranslation();

  const isSettings = match.path === paths.adminSettings;
  const isLocations = match.path === paths.adminSettingsLocations;
  const isDisclosures = match.path === paths.adminSettingsDisclosures;
  const isPolicies = match.path === paths.adminSettingsPolicies;
  const isReferrals = match.path === paths.adminSettingsReferrals;
  const isStarter = client.feature_plan_type === FeaturePlanType.F;

  return (
    <Grid container direction="column" spacing={3}>
      <Grid item xs>
        <Grid container alignItems="center">
          <Grid item xs>
            <Typography color="textSecondary" variant="h5">
              {t('admin:workspaceSettings')}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              size="large"
              className={classes.button}
              component={Links.AdminSettingsAccount}
              startIcon={<SettingsIcon />}
            >
              {t('admin:workspaceEditSettings')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs>
        <PaddedPaper>
          <Settings />
        </PaddedPaper>
      </Grid>
      {isStarter && (
        <Grid item xs>
          <Paper>
            <Tabs value={0}>
              <Tab label={t('admin:departments')} />
              <Tab label={t('admin:locations')} />
            </Tabs>
            <Grid>
              <PaddedPaper padding={120}>
                <Box textAlign="center" maxWidth={300} margin="0 auto">
                  <img width={300} {...getAppImage('illustrations/thinking.png')} />
                  <Box paddingTop={2} paddingBottom={2}>
                    <Typography variant="subtitle2">
                      {t('admin:workspaceSettingsPitchHeadline')}
                    </Typography>
                    <Typography className={classes.lineHeight} variant="subtitle1">
                      {t('admin:workspaceSettingsPitchDescription')}
                    </Typography>
                  </Box>
                  <Button
                    component={Links.AdminUpgrade}
                    color="primary"
                    size="large"
                    variant="contained"
                  >
                    {t('admin:workspaceSettingsPitchButton')}
                  </Button>
                </Box>
              </PaddedPaper>
            </Grid>
          </Paper>
        </Grid>
      )}
      <Capability name="Admin.Settings.Account.Function.Departments">
        <Grid item xs>
          <Paper>
            <Tabs value={match.path}>
              <LargeTab
                component={Links.AdminSettings}
                label={t('admin:departments')}
                value={paths.adminSettings}
                capability="Admin.Settings.Account.Function.Departments"
              />
              <LargeTab
                component={Links.AdminSettingsLocations}
                label={t('admin:locations')}
                value={paths.adminSettingsLocations}
                capability="Admin.Settings.Account.Function.Locations"
              />
              <LargeTab
                component={Links.AdminSettingsDisclosures}
                label={t('admin:disclosures')}
                value={paths.adminSettingsDisclosures}
                capability="Admin.Settings.Account.Function.Disclosures"
              />
              <LargeTab
                component={Links.AdminSettingsPolicies}
                label={t('admin:policies')}
                value={paths.adminSettingsPolicies}
                capability="Admin.Settings.Account.Function.Policies"
              />
              <LargeTab
                component={Links.AdminSettingsReferrals}
                label={t('admin:referrals')}
                value={paths.adminSettingsReferrals}
                capability="Admin.Settings.Account.Function.Referrals"
              />
            </Tabs>
            <Grid>
              <PaddedPaper padding={40}>
                {isSettings && (
                  <Queries.GetClientDepartments loader={<Loader />}>
                    {({ client_departments }) => (
                      <DepartmentsLocations
                        list={client_departments}
                        listType={EditableListType.Departments}
                      />
                    )}
                  </Queries.GetClientDepartments>
                )}
                {isLocations && (
                  <Queries.GetClientLocations loader={<Loader />}>
                    {({ client_locations }) => (
                      <DepartmentsLocations
                        list={client_locations}
                        listType={EditableListType.Locations}
                      />
                    )}
                  </Queries.GetClientLocations>
                )}
                {isDisclosures && (
                  <Queries.GetAllClientDisclosures loader={<Loader />}>
                    {({ client_disclosures }) => <Disclosures list={client_disclosures} />}
                  </Queries.GetAllClientDisclosures>
                )}
                {isPolicies && <Policies />}
                {isReferrals && <Referrals />}
              </PaddedPaper>
            </Grid>
          </Paper>
        </Grid>
      </Capability>
    </Grid>
  );
};

export default Account;
